import {Select} from '@base-ui/react/select';
import styled from '@emotion/styled';
import React from 'react';

import {ChevronSmallDown, ChevronSmallUp} from '@ELib/assets/images';
import {
  mixinCenterWrapper,
  mixinSVGIcon,
  mixinTruncate,
} from '@ELib/styles/mixins';

export type ELabelSelectOption = {
  value: string;
  displayText: string;
};

export type ELabelSelectProps = {
  value?: string;
  name?: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: ELabelSelectOption[];
  ariaLabel?: string;
};

export const ELabelSelect = ({
  value,
  name,
  onChange,
  options,
  ariaLabel,
}: ELabelSelectProps): React.ReactElement => {
  // Base UI's Select.Value renders the label looked up from this map.
  const items = Object.fromEntries(
    options.map((o) => [o.value, o.displayText])
  );

  const compatibilityOnChange = (newValue: string | null) => {
    // Base UI reports null when the current value is no longer among the
    // options (e.g. the options changed). Mirror the previous behaviour of
    // not emitting a change in that case.
    if (newValue == null) {
      return;
    }
    onChange({
      type: 'change',
      target: {
        name,
        value: newValue,
      },
    } as React.ChangeEvent<HTMLSelectElement>);
  };

  return (
    <Select.Root
      items={items}
      value={value}
      onValueChange={compatibilityOnChange}
    >
      <StyledTrigger aria-label={ariaLabel}>
        <StyledValue />
        <ChevronSmallDown className='chevron chevron-closed' />
        <ChevronSmallUp className='chevron chevron-open' />
      </StyledTrigger>
      <Select.Portal>
        {/* alignItemWithTrigger={false} keeps the classic dropdown-below
            placement instead of Base UI's default select-like overlay */}
        <StyledPositioner alignItemWithTrigger={false}>
          <StyledPopup>
            {options.map((o) => (
              <StyledItem key={o.value} value={o.value}>
                <Select.ItemText>{o.displayText}</Select.ItemText>
              </StyledItem>
            ))}
          </StyledPopup>
        </StyledPositioner>
      </Select.Portal>
    </Select.Root>
  );
};

const StyledTrigger = styled(Select.Trigger)`
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-thin);

  color: var(--color-core-primary);
  background: var(--color-core-white);

  border: none;

  padding: 0;
  margin-bottom: var(--gutter-tiny);
  ${mixinTruncate}
  ${mixinCenterWrapper}

  &:focus-visible {
    outline: var(--focus-border-width) solid var(--color-core-keyboardfocus);
  }

  &[aria-expanded='true'] {
    outline: var(--focus-border-width) solid var(--color-core-keyboardfocus);
  }

  svg {
    ${mixinSVGIcon}

    color: var(--color-state-active);
    padding-left: var(--gutter-tiny);
  }

  .chevron-open {
    display: none;
  }

  &[aria-expanded='true'] {
    .chevron-open {
      display: block;
    }

    .chevron-closed {
      display: none;
    }
  }
`;

const StyledValue = styled(Select.Value)`
  ${mixinTruncate}
`;

const StyledPositioner = styled(Select.Positioner)`
  z-index: var(--zindex-dropdown);
`;

const StyledPopup = styled(Select.Popup)`
  overflow: auto;
  max-height: var(--dropdown-max-height);
  background: var(--color-core-white);
  border: var(--regular-border-width) solid var(--color-menu-border-focus);
  list-style: none;
  padding: 0;
  margin: 0;
`;

const StyledItem = styled(Select.Item)`
  color: var(--color-core-link);
  padding: var(--gutter-little) var(--gutter);
  font-weight: var(--dropdown-item-title);
  /* the highlight background is the focus indicator, as before */
  outline: none;

  &[data-highlighted],
  &:hover {
    background-color: var(--color-state-hover);
    color: var(--color-core-black);
    cursor: pointer;
  }
`;
